// @ts-strict-ignore
import { BaseArticleOperation } from '@shared/undo/operation/base-article-operation';
import { IOperation } from '@shared/undo/operation/i-operation';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CopyConnectionItem } from '../components/article/copy-properties-modal/copy-connection-item';
import { CopyFromOperationState } from '../components/article/copy-properties-modal/copy-from-operation-state';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ProjectArticleManager } from '@project/components/article/manager/project-article.manager';
import { ProjectModel } from '@project/model/project.model';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionConverter } from '@article/components/connections/connection-creation/connection-converter';
import { ConnectionCreator } from '@article/components/connections/connection-creation/connection-creator';
import { AdminService } from '../services/admin.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ConnectionUtils } from '@3d-scene/model3d/util/connection-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';

export interface CopyProperties {
  copyConnectionProperties: boolean;
  copyBehaviorProperties: boolean;
  copyPlausiProperties: boolean;
}

export class CopyArticlePropertiesFromOperation extends BaseArticleOperation implements IOperation {
  private _copyProperties: CopyProperties;

  constructor(
    private _sourceArticleId: number,
    articleContainer: ArticleContainer,
    private _copyConnectionItems: CopyConnectionItem[],
    private _oldState: CopyFromOperationState,
    private _newState: CopyFromOperationState,
    copyProperties: CopyProperties,
    projectModel: ProjectModel,
    private projectArticleManager: ProjectArticleManager,
    private selectionModelService: SelectionModelService,
    private sceneManager: SceneManager,
    articleContainerFactory: ArticleContainerFactory,
    private connectionUtils: ConnectionUtils,
    containerService: ContainerService,
    private connectionCreator: ConnectionCreator,
    private connectionConverter: ConnectionConverter,
    private adminService: AdminService,
    private applicationRefreshService: ApplicationRefreshService
  ) {
    super(
      projectModel,
      articleContainerFactory,
      containerService,
      articleContainer
    );
    this._copyProperties = JSON.parse(JSON.stringify(copyProperties)); // deepCopy
  }

  public getQualifiedClassName(): string {
    return 'CopyArticlePropertiesFromOperation';
  }

  public getOperationDescription(): string {
    return 'changeArticleConnectionsOperation.description';
  }

  public async performRedo(): Promise<void> {
    this.updateCurrentArticleByArticleVO(this._copyConnectionItems, this._newState, this._copyProperties);
  }

  public async performUndo(): Promise<void> {
    this.updateCurrentArticleByArticleVO(this._copyConnectionItems, this._oldState, this._copyProperties, true);
  }

  private updateCurrentArticleByArticleVO(
    copyConnectionsItems: CopyConnectionItem[],
    state: CopyFromOperationState,
    copyProperties: CopyProperties,
    isUndoOperation: boolean = false
  ): void {
    this.selectionModelService.removeAllSelections();

    if (this._articleContainer && copyConnectionsItems.length) {
      copyConnectionsItems.forEach((connectionItem: CopyConnectionItem) => {
        const connection: Connection = ConnectionInfoUtils.findMainConnectionIgnoringPostfix(
          this._articleContainer.connections,
          connectionItem.connectionName
        );
        const connectionState: Connection = ConnectionInfoUtils.findMainConnectionIgnoringPostfix(state.connections, connectionItem.connectionName);

        if (connection && connectionItem.copyAttributes) {
          if (connectionState) {
            this.connectionUtils.replaceTargetBySourceConnection(
              this.connectionConverter,
              connectionItem.connectionName,
              state.connections,
              connectionItem.connectionName,
              this._articleContainer,
              true
            );
          } else {
            this.removeConnection(connection);
          }
        }

        if (connection && connectionState && connectionItem.copyProperties) {
          this.updateConnectionProperties(connectionItem.connectionName, connectionState, isUndoOperation);
        }

        if (!connection && connectionState) {
          ConnectionInfoUtils.findLinkedConnectionsInSource(connectionState, state.connections).forEach((c: Connection) => {
            this.connectionUtils.createTargetBySourceConnection(this._articleContainer, c);
          });
        }
      });
    }

    ArticleUtils.replaceTargetArticlePropertiesBySourceArticle(
      this.getArticle(),
      state.article,
      copyProperties.copyConnectionProperties,
      copyProperties.copyBehaviorProperties,
      copyProperties.copyPlausiProperties,
      isUndoOperation
    );

    this.sceneManager.renderScene();
    this.applicationRefreshService.notifyProjectChange();
    this.adminService.updateProperties();
  }

  private updateConnectionProperties(connectionNameWithoutPostfix: string, sourceConnection: Connection, isUndoOperation: boolean): void {
    const targetConnection: Connection = ConnectionInfoUtils.findMainConnectionIgnoringPostfix(
      this._articleContainer.connections,
      connectionNameWithoutPostfix
    );
    if (targetConnection && targetConnection.metaElementVO) {
      ConnectionInfoUtils.replaceTargetBySourceMetaData(targetConnection.metaElementVO, sourceConnection.metaElementVO, isUndoOperation);
      this.connectionUtils.updateInternalConnectionProperties(targetConnection.metaElementVO, targetConnection.article);
    }
  }

  private getArticle(): ArticleVO {
    if (this._articleContainer) {
      return this._articleContainer.article;
    } else if (this.projectModel.isCompositeAdminView) {
      const articleUsage: ArticleUsageVO = this.projectModel.getCurrentLoadedArticleUsage();
      return this.projectArticleManager.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
    }
  }

  private removeConnection(connection: Connection): void {
    const linkedConnections: Connection[] = this.connectionCreator.getMultipurposeConnectionsBySelection(connection);
    linkedConnections.forEach((c) => {
      this._articleContainer.disposeConnectionByName(c.connectionName);
      this.connectionConverter.removeMetaElementByName(this._articleContainer.article, c.connectionName);
    });
  }

  public equals(operation: any): boolean {
    if (!(operation instanceof CopyArticlePropertiesFromOperation)) {
      return false;
    }
    if (this._sourceArticleId !== operation._sourceArticleId) {
      return false;
    }
    if (this._copyProperties.copyBehaviorProperties !== operation._copyProperties.copyBehaviorProperties
      || this._copyProperties.copyPlausiProperties !== operation._copyProperties.copyPlausiProperties
      || this._copyProperties.copyConnectionProperties !== operation._copyProperties.copyConnectionProperties) {
      return false;
    }
    for (const item of this._copyConnectionItems) {
      const operationItem = operation._copyConnectionItems.find(i => i.connectionName === item.connectionName);
      if (!operationItem
        || item.copyProperties !== operationItem.copyProperties
        || item.copyAttributes !== operationItem.copyAttributes
        || item.isNewConnection !== operationItem.isNewConnection
        || item.sourceConnectionName !== operationItem.sourceConnectionName) {
        return false;
      }
    }
    return true;
  }
}
